import React, {ReactNode, useCallback} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CardHeader,
    IconButton,
    Link,
    ListItem,
    ListItemText,
    Skeleton,
    Stack, Tooltip,
    Typography
} from "@mui/material";
import {RemoteValue, useRemoteValue} from "../../util/RemoteValue";
import {Loading} from "../../components/loading-data/LoadingData";
import {Link as RouterLink} from "react-router-dom";
import {PermissionCheck} from "../../util/access/Access";
import {usePermission} from "../../util/access/UseAccess";
import {Virtuoso} from "react-virtuoso";
import {CsvModel} from "../../util/CSVWriteUtil";
import {Opt} from "../../util/Optional";
import DownloadIcon from "@mui/icons-material/Download";
import FixIcon from "@mui/icons-material/Construction";

export function DashboardWidget<Problem>({title, description, problems, link, requiredPermissions, renderProblem, csvModel, fixAction}: {
    title: string,
    description?: string,
    problems: RemoteValue<Problem[]>,
    link?: string,
    requiredPermissions: PermissionCheck,
    renderProblem: (problem: Problem, index: number) => ReactNode,
    csvModel?: Opt<RemoteValue<CsvModel<Problem>>>,
    fixAction?: Opt<() => void>,
}) {
    const {canView} = usePermission({canView: requiredPermissions});
    if (!canView) return null;

    return (
        <Card sx={{width: '100%', height: 480, display: 'flex', flexDirection: 'column'}}>
            <CardHeader
                title={(
                    <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Stack direction="row" alignItems="center" gap={2}>
                            <Typography variant="h6">{title}</Typography>
                            <Loading value={problems} skeletonValue={[]}>{problems => link ? (
                                <Link component={RouterLink} to={link} underline="none" variant="body2" sx={{':hover': {backgroundColor: t => t.alpha(t.palette.primary.main, t.palette.action.hoverOpacity)}, px: 1, borderRadius: '4px'}}>
                                    {problems.length}
                                </Link>
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    {problems.length}
                                </Typography>
                            )}</Loading>
                        </Stack>
                        <Stack direction="row" alignItems="center" gap={1}>
                            {fixAction && (
                                <FixActionButton fixAction={fixAction} problems={problems}/>
                            )}
                            {csvModel && (
                                <DownloadCsvButton fileName={title} problems={problems} csvModel={csvModel}/>
                            )}
                        </Stack>
                    </Stack>
                )}
                subheader={description}
                slotProps={{title: {variant: 'h6'}}}
            />
            <CardContent sx={{pt: 0, flexGrow: 1, minHeight: 0, display: 'flex', flexDirection: 'column'}}>
                <Loading
                    value={problems}
                    loading={<Skeleton variant="rounded" height="100%"/>}
                    errorElement={<Alert severity="error">Failed to load this widget.</Alert>}
                >{items => (
                    <Stack sx={{minHeight: 0, flexGrow: 1}}>
                        {items.length > 0 ? (
                            <Box sx={{height: '100%', minHeight: 0, flex: 1, mr: -2}}>
                                <Virtuoso
                                    style={{height: '100%'}}
                                    data={items}
                                    itemContent={(index, item) => (
                                        <Box sx={{pr: 2}}>
                                            {renderProblem(item, index)}
                                        </Box>
                                    )}
                                />
                            </Box>
                        ) : (
                            <Box sx={{display: 'grid', placeItems: 'center', flexGrow: 1}}>
                                <Typography>🌿 No current problems</Typography>
                            </Box>
                        )}
                    </Stack>
                )}</Loading>
            </CardContent>
        </Card>
    );
}

export function DashboardWidgetItem({index, subIndex, left, right}: {
    index: number,
    subIndex?: number,
    left: ReactNode;
    right?: ReactNode;
}) {
    return (
        <ListItem component="div" disableGutters>
            <ListItemText
                primary={
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Stack direction="row" alignItems="center" gap={1}>
                            <Typography variant="body2" sx={{minWidth: '1.5em', textAlign: 'right', ml: subIndex !== undefined ? 4 : 0}} color="textSecondary">
                                {index + 1}.
                                {subIndex !== undefined ? `${subIndex + 1}.` : ''}
                            </Typography>
                            {left}
                        </Stack>
                        {right && (
                            <Stack direction="row" alignItems="center" gap={1}>
                                {right}
                            </Stack>
                        )}
                    </Stack>
                }
            />
        </ListItem>
    );
}

function DownloadCsvButton<Problem>({fileName, problems, csvModel}: {
    fileName: string,
    problems: RemoteValue<Problem[]>,
    csvModel: RemoteValue<CsvModel<Problem>>,
}) {
    const handleDownload = useCallback(async () => {
        (await csvModel.value).downloadCsv(fileName, await problems.value);
    }, [fileName, problems, csvModel]);

    return (
        <Tooltip title="Download as CSV">
            <IconButton size="small" color="primary" onClick={handleDownload}>
                <DownloadIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    );
}

function FixActionButton<Problem>({fixAction, problems: problemsRV}: {
    fixAction: () => void,
    problems: RemoteValue<Problem[]>,
}) {
    const problems = useRemoteValue(problemsRV, []);

    return (
        <Tooltip title="Fix the problems">
            <IconButton size="small" color="primary" onClick={fixAction} disabled={problems.length === 0}>
                <FixIcon fontSize="small"/>
            </IconButton>
        </Tooltip>
    );
}
